import { stars } from '../utils';
import { CreateDocument, CreateElementNode, CreateTextNode, SetNodeData, SetStyleData, SetNodeAttribute, SetNodeURLBasedAttribute, RemoveNodeAttribute, MoveNode, RemoveNode, } from '../messages';
function isSVGElement(node) {
    return node.namespaceURI === 'http://www.w3.org/2000/svg';
}
function isIgnored(node) {
    if (node instanceof Text) {
        return false;
    }
    if (!(node instanceof Element)) {
        return true;
    }
    const tag = node.tagName.toUpperCase();
    if (tag === 'LINK') {
        return !(node.getAttribute('rel').includes('stylesheet') || node.getAttribute('as') === "style");
    }
    return (tag === 'SCRIPT' ||
        tag === 'NOSCRIPT' ||
        tag === 'META' ||
        tag === 'TITLE' ||
        tag === 'BASE');
}
export default class Observer {
    constructor(app, opts) {
        this.app = app;
        this.options = opts;
        this.observer = new MutationObserver(this.app.safe((mutations) => {
            for (const mutation of mutations) {
                const target = mutation.target;
                if (isIgnored(target) || !document.contains(target)) {
                    continue;
                }
                const type = mutation.type;
                if (type === 'childList') {
                    for (let i = 0; i < mutation.removedNodes.length; i++) {
                        this.bindTree(mutation.removedNodes[i]);
                    }
                    for (let i = 0; i < mutation.addedNodes.length; i++) {
                        this.bindTree(mutation.addedNodes[i]);
                    }
                    continue;
                }
                const id = this.app.nodes.getID(target);
                if (id === undefined) {
                    continue;
                }
                if (id >= this.recents.length) {
                    this.recents[id] = undefined;
                }
                if (type === 'attributes') {
                    const name = mutation.attributeName;
                    if (name === null) {
                        continue;
                    }
                    let attr = this.attributesList[id];
                    if (attr === undefined) {
                        this.attributesList[id] = attr = new Set();
                    }
                    attr.add(name);
                    continue;
                }
                if (type === 'characterData') {
                    this.textSet.add(id);
                    continue;
                }
            }
            this.commitNodes();
        }));
        this.commited = [];
        this.recents = [];
        this.indexes = [0];
        this.attributesList = [];
        this.textSet = new Set();
        this.textMasked = new Set();
    }
    clear() {
        this.commited.length = 0;
        this.recents.length = 0;
        this.indexes.length = 1;
        this.attributesList.length = 0;
        this.textSet.clear();
        this.textMasked.clear();
    }
    sendNodeAttribute(id, node, name, value) {
        if (isSVGElement(node)) {
            if (name.substr(0, 6) === 'xlink:') {
                name = name.substr(6);
            }
            if (value === null) {
                this.app.send(RemoveNodeAttribute(id, name));
            }
            else {
                this.app.send(SetNodeAttribute(id, name, value));
            }
            return;
        }
        if (name === 'src' ||
            name === 'srcset' ||
            name === 'integrity' ||
            name === 'crossorigin' ||
            name === 'autocomplete' ||
            name.substr(0, 2) === 'on') {
            return;
        }
        if (name === 'value' &&
            node instanceof HTMLInputElement &&
            node.type !== 'button' &&
            node.type !== 'reset' &&
            node.type !== 'submit') {
            return;
        }
        if (value === null) {
            this.app.send(RemoveNodeAttribute(id, name));
            return;
        }
        if (name === 'style' || name === 'href' && node instanceof HTMLLinkElement) {
            const baseURL = location.origin + location.pathname;
            this.app.send(SetNodeURLBasedAttribute(id, name, value, baseURL));
            return;
        }
        if (name === 'href' || value.length > 1e5) {
            value = '';
        }
        this.app.send(SetNodeAttribute(id, name, value));
    }
    sendNodeData(id, parentElement, data) {
        if (parentElement.tagName.toUpperCase() === 'STYLE') {
            const baseURL = location.origin + location.pathname;
            this.app.send(SetStyleData(id, data, baseURL));
            return;
        }
        if (this.textMasked.has(id)) {
            data = data.replace(/[^\f\n\r\t\v\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]/g, '█');
        }
        else {
            if (this.options.obscureTextNumbers) {
                data = data.replace(/\d/g, '0');
            }
            if (this.options.obscureTextEmails) {
                data = data.replace(/([^\s]+)@([^\s]+)\.([^\s]+)/g, (...f) => stars(f[1]) + '@' + stars(f[2]) + '.' + stars(f[3]));
            }
        }
        this.app.send(SetNodeData(id, data));
    }
    bindNode(node) {
        const r = this.app.nodes.registerNode(node);
        const id = r[0];
        this.recents[id] = r[1] || this.recents[id] || false;
    }
    bindTree(node) {
        if (isIgnored(node)) {
            return;
        }
        this.bindNode(node);
        const walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT + NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => isIgnored(node) || this.app.nodes.getID(node) !== undefined
                ? NodeFilter.FILTER_REJECT
                : NodeFilter.FILTER_ACCEPT,
        }, false);
        while (walker.nextNode()) {
            this.bindNode(walker.currentNode);
        }
    }
    unbindNode(node) {
        const id = this.app.nodes.unregisterNode(node);
        if (id !== undefined && this.recents[id] === false) {
            this.app.send(RemoveNode(id));
        }
    }
    _commitNode(id, node) {
        const parent = node.parentNode;
        let parentID;
        if (id !== 0) {
            if (parent === null) {
                this.unbindNode(node);
                return false;
            }
            parentID = this.app.nodes.getID(parent);
            if (parentID === undefined) {
                this.unbindNode(node);
                return false;
            }
            if (!this.commitNode(parentID)) {
                this.unbindNode(node);
                return false;
            }
            if (this.textMasked.has(parentID) ||
                (node instanceof Element && node.hasAttribute('data-asayer-masked'))) {
                this.textMasked.add(id);
            }
            let sibling = node.previousSibling;
            while (sibling !== null) {
                const siblingID = this.app.nodes.getID(sibling);
                if (siblingID !== undefined) {
                    this.commitNode(siblingID);
                    this.indexes[id] = this.indexes[siblingID] + 1;
                    break;
                }
                sibling = sibling.previousSibling;
            }
            if (sibling === null) {
                this.indexes[id] = 0;
            }
        }
        const isNew = this.recents[id];
        const index = this.indexes[id];
        if (index === undefined) {
            throw 'commitNode: missing node index';
        }
        if (isNew === true) {
            if (node instanceof Element) {
                if (parentID !== undefined) {
                    this.app.send(CreateElementNode(id, parentID, index, node.tagName, isSVGElement(node)));
                }
                for (let i = 0; i < node.attributes.length; i++) {
                    const attr = node.attributes[i];
                    this.sendNodeAttribute(id, node, attr.nodeName, attr.value);
                }
            }
            else if (node instanceof Text) {
                // for text node id != 0, hence parentID !== undefined and parent is Element
                this.app.send(CreateTextNode(id, parentID, index));
                this.sendNodeData(id, parent, node.data);
            }
            return true;
        }
        if (isNew === false && parentID !== undefined) {
            this.app.send(MoveNode(id, parentID, index));
        }
        const attr = this.attributesList[id];
        if (attr !== undefined) {
            if (!(node instanceof Element)) {
                throw 'commitNode: node is not an element';
            }
            for (const name of attr) {
                this.sendNodeAttribute(id, node, name, node.getAttribute(name));
            }
        }
        if (this.textSet.has(id)) {
            if (!(node instanceof Text)) {
                throw 'commitNode: node is not a text';
            }
            // for text node id != 0, hence parent is Element
            this.sendNodeData(id, parent, node.data);
        }
        return true;
    }
    commitNode(id) {
        const node = this.app.nodes.getNode(id);
        if (node === undefined) {
            return false;
        }
        const cmt = this.commited[id];
        if (cmt !== undefined) {
            return cmt;
        }
        return (this.commited[id] = this._commitNode(id, node));
    }
    commitNodes() {
        let node;
        for (let id = 0; id < this.recents.length; id++) {
            this.commitNode(id);
            if (this.recents[id] === true && (node = this.app.nodes.getNode(id))) {
                this.app.nodes.callNodeCallbacks(node);
            }
        }
        this.clear();
    }
    observe() {
        this.observer.observe(document, {
            childList: true,
            attributes: true,
            characterData: true,
            subtree: true,
            attributeOldValue: false,
            characterDataOldValue: false,
        });
        this.app.send(CreateDocument());
        this.bindTree(document.documentElement);
        this.commitNodes();
    }
    disconnect() {
        this.observer.disconnect();
        this.clear();
    }
}
